import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, Partnership } from '@nexus/shared/types';
import { toast } from './toastStore';

interface PartnershipsState {
  items: Partnership[];
  total: number;
  isLoading: boolean;
  error: string | null;
  fetch: (params?: Record<string, string | number | undefined>) => Promise<void>;
  create: (data: Record<string, unknown>) => Promise<Partnership>;
  update: (id: string, data: Record<string, unknown>) => Promise<void>;
  remove: (id: string) => Promise<void>;
  checkin: (id: string) => Promise<void>;
  clearError: () => void;
}

function convert(p: Record<string, unknown>): Partnership {
  return {
    ...p,
    createdAt: fromUnix(p.createdAt as number),
    updatedAt: fromUnix(p.updatedAt as number),
    contractStart: p.contractStart ? fromUnix(p.contractStart as number) : undefined,
    contractEnd: p.contractEnd ? fromUnix(p.contractEnd as number) : undefined,
    nextCheckinAt: p.nextCheckinAt ? fromUnix(p.nextCheckinAt as number) : undefined,
    lastCheckinAt: p.lastCheckinAt ? fromUnix(p.lastCheckinAt as number) : undefined,
  } as Partnership;
}

export const usePartnershipsStore = create<PartnershipsState>((set) => ({
  items: [],
  total: 0,
  isLoading: false,
  error: null,

  fetch: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<Partnership[]>>(`/api/partnerships${toQueryString(params)}`);
      set({
        items: (res.data ?? []).map(p => convert(p as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  create: async (data) => {
    try {
      const res = await api.post<ApiResponse<Partnership>>('/api/partnerships', data);
      const p = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ items: [p, ...s.items], total: s.total + 1 }));
      toast.success('Partnership created');
      return p;
    } catch (err) {
      toast.error('Failed to create partnership', (err as Error).message);
      throw err;
    }
  },

  update: async (id, data) => {
    try {
      const res = await api.patch<ApiResponse<Partnership>>(`/api/partnerships/${id}`, data);
      const p = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ items: s.items.map((i) => (i.id === id ? p : i)) }));
      toast.success('Partnership updated');
    } catch (err) {
      toast.error('Failed to update partnership', (err as Error).message);
      throw err;
    }
  },

  remove: async (id) => {
    try {
      await api.del(`/api/partnerships/${id}`);
      set((s) => ({ items: s.items.filter((i) => i.id !== id), total: s.total - 1 }));
      toast.success('Partnership deleted');
    } catch (err) {
      toast.error('Failed to delete partnership', (err as Error).message);
      throw err;
    }
  },

  checkin: async (id) => {
    try {
      const res = await api.post<ApiResponse<Partnership>>(`/api/partnerships/${id}/checkin`);
      const p = convert(res.data as unknown as Record<string, unknown>);
      set((s) => ({ items: s.items.map((i) => (i.id === id ? p : i)) }));
      toast.success('Check-in recorded');
    } catch (err) {
      toast.error('Failed to record check-in', (err as Error).message);
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
